'use client'

import Container from '@/components/structure/Container'
import Section from '@/components/content/common/Section'
import Tree from '@/components/content/sidebar/Tree'
import RichText from '@/components/content/common/RichText'
import CoursesSidebar from '@/components/content/courses/CourseSidebar'
import Testimonials, { generateRating } from '@/components/ui/Testimonials'
import CourseCallToAction from './CourseCallToAction'
import CourseOverview from './CourseOverview'
import CourseInstructors from './CourseInstructors'
import VideoPlayer from '@/components/content/videos/VideoPlayer'
import { Icon } from '@/icons/Icon'
import { PageContextProvider } from '@/components/content/common/PageContext'
import { extractBlogSlugUrlFromFaq } from '@/lib/formatting'
import { isPopulatedObject, toSlug } from '@/lib/utils'
import { courseTestimonials } from '@/data/testimonials'
import Timestamp from '@/components/ui/Timestamp'
import PreferredSource from '@/components/ui/PreferredSource'
import type { SectionItem, Block } from '@/types/app'
import type {
  App,
  Course,
  CoursesFaq,
  Faq,
  Product,
  ProductCourse,
  ProductPrice,
  Video,
  User,
} from '@/payload-types'

function demoteHeadings(blocks: Block[]): Block[] {
  return blocks.map((block) => {
    if ('tag' in block && typeof block.tag === 'string' && /^h[1-5]$/.test(block.tag)) {
      const level = parseInt(block.tag.charAt(1), 10)
      return { ...block, tag: `h${Math.min(level + 1, 6)}` }
    }
    return block
  })
}

const DEMOTED_HEADING_STYLES =
  '[&_h3]:text-18 [&_h3]:font-bold md:[&_h3]:text-20 [&_h4]:text-18 [&_h4]:font-bold'

function getArticle(word: string): 'A' | 'An' {
  const vowels = ['a', 'e', 'i', 'o', 'u']
  const firstChar = word.charAt(0).toLowerCase()
  return vowels.includes(firstChar) ? 'An' : 'A'
}

function extractProductData(course: Course): {
  price: string | null
  checkoutUrl: string | null
} {
  const productCourseDoc = course.productCourse?.docs?.[0]
  if (!isPopulatedObject<ProductCourse>(productCourseDoc)) {
    return { price: null, checkoutUrl: null }
  }

  const product = productCourseDoc.product
  if (!isPopulatedObject<Product>(product)) {
    return { price: null, checkoutUrl: null }
  }

  const checkoutUrl = product.checkoutUrl ?? null

  const priceDoc = (product.prices?.docs ?? [])
    .filter((p): p is ProductPrice => isPopulatedObject<ProductPrice>(p) && !p.deletedAt)
    .at(0)
  if (!priceDoc) {
    return { price: null, checkoutUrl }
  }

  const cents = priceDoc.price
  const dollars = (cents / 100).toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  return { price: `$${dollars} USD`, checkoutUrl }
}

const requirements = (app: string) => [
  {
    icon: 'alarm-clock',
    text: 'A laptop or desktop with reliable internet access',
  },
  {
    icon: 'anchor',
    text: `${getArticle(app)} ${app} account.`,
  },
  {
    icon: 'bag-shopping',
    text: 'Basic familiarity with SaaS tools.',
  },
  {
    icon: 'ball-basket',
    text: 'A willingness to experiment and iterate.',
  },
  {
    icon: 'award',
    text: 'Commitment to improving your daily habits and tooling.',
  },
]

type CoursesContainerProps = {
  course: Course
  instructor: Pick<User, 'id' | 'name' | 'image' | 'authorDescription' | 'headshot'>
}

export default function CoursesContainer({ course, instructor }: CoursesContainerProps) {
  return (
    <PageContextProvider scrollOffset={145}>
      <CoursesContainerContent course={course} instructor={instructor} />
    </PageContextProvider>
  )
}

function CoursesContainerContent({ course, instructor }: CoursesContainerProps) {
  const app = course?.app as App | undefined
  if (!app) {
    throw new Error('App not populated')
  }

  const { price, checkoutUrl } = extractProductData(course)
  const summaryContent = (course.summary?.root?.children as Block[] | undefined) ?? []

  const faqs = (course.courseFaqs?.docs ?? [])
    .filter((doc): doc is CoursesFaq => doc !== null)
    .map((doc) => doc.faq)
    .filter((faq): faq is Faq => faq !== null)

  const showTestimonials = app.name === 'Motion'
  const video = isPopulatedObject<Video>(course.overviewVideo) && course.overviewVideo

  // Summary
  const summarySection = [
    {
      title: `${course.name} Summary`,
      content: (
        <div id="summary" className="flex flex-col space-y-4">
          <div className={DEMOTED_HEADING_STYLES}>
            <RichText id={app.slug} content={demoteHeadings(summaryContent)} />
          </div>
          {video && <VideoPlayer video={video} />}
          <CourseCallToAction
            trustedBy={course.trustedBy || undefined}
            duration={course.duration || undefined}
            price={price ?? undefined}
            checkoutUrl={checkoutUrl}
          />
        </div>
      ),
    },
  ]

  // Requirements
  const requirementsSection = [
    {
      title: `Requirements`,
      content: (
        <div className="flex flex-col gap-3">
          {requirements(app.name).map((requirement, requirementIndex) => {
            return (
              <div key={requirementIndex} className="flex gap-4">
                <Icon name={requirement.icon} className="pt-3 text-primary-500" />
                <p className="text-gray-950 dark:text-white">{requirement.text}</p>
              </div>
            )
          })}
        </div>
      ),
    },
  ]

  // Curriculum
  const curriculumSection =
    course.curriculum?.root?.children && course.curriculum.root.children.length > 0
      ? [
          {
            title: `Curriculum`,
            content: (
              <div className={DEMOTED_HEADING_STYLES}>
                <RichText content={demoteHeadings(course.curriculum.root.children as Block[])} />
              </div>
            ),
          },
        ]
      : []

  // Instructors
  const instructorsSection = [
    {
      title: `Instructors`,
      content: <CourseInstructors app={app.name} instructor={instructor} />,
    },
  ]

  // Testimonials
  const testimonialsSection = showTestimonials
    ? [
        {
          title: `Testimonials`,
          content: (
            <div className="flex flex-col gap-4">
              {courseTestimonials?.map((testimonial, testimonialIndex) => {
                return (
                  <div
                    className="flex flex-col gap-2.5 rounded-2xl border border-gray-200 px-5 py-4 dark:border-white/10"
                    key={testimonialIndex}
                  >
                    <div className="flex items-center justify-between gap-4">
                      <p className="font-semibold text-gray-950 dark:text-white">
                        {testimonial.name}
                      </p>
                      {generateRating(testimonial.rating)}
                    </div>
                    <p className="italic text-gray-600 dark:text-white/60">{testimonial.text}</p>
                  </div>
                )
              })}
            </div>
          ),
        },
      ]
    : []

  // FAQ
  const faqSection: SectionItem[] = [
    {
      title: 'FAQ',
      content: (
        <div className="flex flex-col gap-4">
          {faqs?.map((section, sectionIndex) => {
            const faq = section as Faq
            return (
              <Section
                key={`faq-${sectionIndex}`}
                sectionId={`faq-${toSlug(faq.question ?? 'Untitled')}`}
                title={faq.question ?? 'Untitled'}
                isChild={true}
                offset={95}
                icon={<Icon name="circle-question" />}
                collapsed={sectionIndex > 0}
                renderCollapsedContent={true}
                ignoreHeading={false}
              >
                {faq.answer?.root?.children ? (
                  <RichText
                    content={faq.answer.root.children as Block[]}
                    readMore={extractBlogSlugUrlFromFaq(faq)}
                  />
                ) : (
                  <p>No answer available</p>
                )}
              </Section>
            )
          })}
        </div>
      ),
    },
  ]

  // Structure
  const content: SectionItem[] = [
    ...summarySection,
    ...requirementsSection,
    ...curriculumSection,
    ...instructorsSection,
    ...testimonialsSection,
    ...(faqs.length > 0 ? faqSection : []),
  ]

  return (
    <>
      {showTestimonials && <Testimonials testimonials={courseTestimonials} />}
      <Container
        sidebar={
          <>
            <Tree
              roots={content}
              offset={96}
              header={
                <CoursesSidebar
                  course={course}
                  app={app}
                  video={video ? true : false}
                  price={price}
                  customLink={checkoutUrl ?? undefined}
                />
              }
              toggle={false}
            />
            <CourseOverview
              duration={course.duration}
              modules={course.moduleCount}
              className="hidden md:flex"
            />
            <PreferredSource invert={true} />
            {course.updatedAt && (
              <Timestamp modified={course.updatedAt} className="hidden md:flex" />
            )}
          </>
        }
        mobilePadding={false}
        toggle={false}
      >
        {content.map((section, index) => (
          <Section
            key={index}
            title={section.title || ''}
            isParent={section.title === 'FAQ' || (section.children?.length ?? 0) > 0}
            offset={96}
            toggle={false}
          >
            {section.content}
          </Section>
        ))}
      </Container>
    </>
  )
}
